<template>
  <v-dialog
    :model-value="modelValue"
    fullscreen
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="bm-overlay" data-testid="release-result" role="dialog" aria-label="Release result">
      <!--
        The frame is a function of what the SERVER says happened (#424), in one
        of four tones carried into the DOM as `data-tone` so each is assertable
        without a computed-style primitive:

          progress -- the batch is still releasing, or the outcome has not been
                      read yet. No verdict is shown: a verdict drawn before the
                      outcome is known was the "All N orders were sent" lie.
          success  -- released and every order was sent.
          split    -- some orders were sent, some were not. A normal outcome
                      (ADR 0066 (4)), not an error frame.
          blocked  -- nothing was sent (or the outcome could not be read).
      -->
      <header class="bm-result__head">
        <div
          class="bm-result__badge"
          :class="`bm-result__badge--${tone === 'success' ? 'success' : tone === 'progress' ? 'progress' : 'split'}`"
          data-testid="result-tone"
          :data-tone="tone"
        >
          <i class="material-icons" aria-hidden="true">{{ icon }}</i>
        </div>
        <div class="bm-result__headings">
          <h2 class="bm-result__title" data-testid="result-headline">{{ headline }}</h2>
          <p class="bm-result__sub" data-testid="result-sub">{{ subline }}</p>
        </div>
      </header>

      <div v-if="known" class="bm-overlay__body">
        <!-- The counts, as three plain numbers that always add up to the batch. -->
        <div class="bm-result__counts" data-testid="result-counts">
          <div>
            <span class="bm-result__count" data-testid="result-sent-count">{{ sent.length }}</span> sent
          </div>
          <div>
            <span class="bm-result__count" data-testid="result-held-count">{{ heldBack.length }}</span> held back
          </div>
          <div>
            <span class="bm-result__count" data-testid="result-notsent-count">{{ notSent.length }}</span> not sent
          </div>
        </div>

        <!-- Held-back orders come FIRST, with the plain reason. -->
        <div v-if="heldBack.length" class="bm-heldback" data-testid="result-held-back">
          <div class="bm-heldback__head">
            <div class="bm-heldback__title">
              Held back &middot; {{ heldBack.length }} {{ heldBack.length === 1 ? 'order' : 'orders' }} &middot;
              {{ money(heldBackTotal) }} not sent
            </div>
            <p class="bm-heldback__body">
              These orders were not sent. They stay in <b>batch {{ batch.id }}</b>; the reason is
              listed under each one.
              <template v-if="anyCardDeclined">
                Where a card was declined, once the shopper records a working payment method the
                order is rebatched and goes out with a later release.
              </template>
            </p>
          </div>
          <div
            v-for="h in heldBack"
            :key="h.order.id"
            class="bm-heldback__row"
            :data-testid="`result-held-${h.order.id}`"
          >
            <div class="bm-heldback__who">
              <div class="bm-heldback__name">{{ h.order.shopper }} &middot; {{ money(orderTotal(h.order)) }}</div>
              <div class="bm-heldback__reason">{{ h.reason }}</div>
              <div v-if="h.moneyLine" class="bm-heldback__money">{{ h.moneyLine }}</div>
            </div>
            <div class="bm-heldback__actions">
              <!-- "Email the shopper" (Locked Decisions) is NOT rendered yet.
                   Gate review 2026-08-20 found the button emitted `notify` with
                   nothing listening -- it rendered, it was asserted to exist,
                   and clicking it did nothing. A dead action on a recovery
                   screen is worse than an absent one: it tells a distributor
                   the shopper has been contacted when nobody has.
                   The send path itself EXISTS (send_batch_charge_failed_to_shopper,
                   merged in #2743); what is missing is an endpoint to invoke it.
                   Restore this button in the same unit that adds that endpoint,
                   and assert the EMIT, not the existence. -->
              <!-- Only when a destination actually exists. Since 2026-09-12
                   release leaves held-back orders attached and sends no
                   `destination_batch_id`, so `nextBatchId` is empty on this
                   path and this rendered a bare "Open " that navigated
                   nowhere. The control stays for the case where a destination
                   IS named; it must not render without one. -->
              <button
                v-if="nextBatchId"
                type="button"
                class="bm-btn"
                :data-testid="`view-next-${h.order.id}`"
                @click="$emit('open-next')"
              >
                Open {{ nextBatchId }}
              </button>
            </div>
          </div>
        </div>

        <!-- Orders that never entered the release: not sent, and not "held back"
             either, because nothing was attempted for them. -->
        <div v-if="notSent.length" class="bm-heldback bm-notsent" data-testid="result-not-sent">
          <div class="bm-heldback__head">
            <div class="bm-heldback__title">
              Not sent &middot; {{ notSent.length }} {{ notSent.length === 1 ? 'order' : 'orders' }}
            </div>
            <p class="bm-heldback__body">
              These orders were not part of this release (for example, the shopper never finished
              checking out), so nothing was sent for them.
            </p>
          </div>
          <div
            v-for="o in notSent"
            :key="o.id"
            class="bm-heldback__row"
            :data-testid="`result-notsent-${o.id}`"
          >
            <div class="bm-heldback__who">
              <div class="bm-heldback__name">{{ o.shopper }} &middot; {{ money(orderTotal(o)) }}</div>
            </div>
          </div>
        </div>

        <!-- ...then the sent block. -->
        <div class="bm-sentblock">
          <div class="bm-sentblock__head">
            <div class="bm-sentblock__title">
              Sent &middot; {{ sent.length }} {{ sent.length === 1 ? 'order' : 'orders' }}
            </div>
            <div class="bm-sentblock__amt" data-testid="result-charged">{{ money(sentTotal) }}</div>
          </div>
          <div
            v-for="o in sentPreview"
            :key="o.id"
            class="bm-sentblock__row"
            :data-testid="`result-sent-${o.id}`"
          >
            <span>{{ o.shopper }}<span class="bm-sentblock__meta"> &middot; sent</span></span>
            <span class="bm-sentblock__rowamt">{{ money(orderTotal(o)) }}</span>
          </div>
          <div v-if="sent.length > sentPreview.length" class="bm-sentblock__more">
            and {{ sent.length - sentPreview.length }} more
          </div>
        </div>

        <p v-if="released" class="bm-result__lock">
          <i class="material-icons bm-result__lockicon" aria-hidden="true">lock</i>
          <span>
            Batch {{ batch.id }} is now Released. Its orders can no longer be edited or cancelled here
            &mdash; contact {{ batch.decoratorTarget }} for production changes.
          </span>
        </p>
        <p v-else class="bm-result__lock" data-testid="result-not-released">
          <i class="material-icons bm-result__lockicon" aria-hidden="true">info</i>
          <span>
            Batch {{ batch.id }} is not released. Open it in the batches list to retry the orders that
            were held back, or to resolve them.
          </span>
        </p>
      </div>

      <div v-else class="bm-overlay__body">
        <p class="bm-result__lock" data-testid="result-pending-note">
          <i class="material-icons bm-result__lockicon" aria-hidden="true">{{ outcomeError ? 'info' : 'sync' }}</i>
          <span v-if="outcomeError">
            Open batch {{ batch.id }} in the batches list to check the state of its orders.
          </span>
          <span v-else>
            This screen updates when the release finishes. You can close it; progress stays on the
            batch in the list.
          </span>
        </p>
      </div>

      <footer class="bm-overlay__foot bm-result__foot">
        <button type="button" class="bm-btn bm-btn--primary" data-testid="result-done" @click="close">
          Back to batches
        </button>
        <button
          v-if="known && (heldBack.length || notSent.length) && nextBatchId"
          type="button"
          class="bm-btn"
          data-testid="result-open-next"
          @click="$emit('open-next')"
        >
          Go to batch {{ nextBatchId }}
        </button>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
/**
 * The outcome screen.
 *
 * #424 (2026-10-21): this screen used to be told which orders were "held" by
 * the caller, derived from payment_state == charge_failed alone. A decorator
 * contract refusal (uncharged, `pending_capture`, item `held_back`) is not a
 * charge failure, so it counted as SENT, and the screen was frozen at the 202
 * response (a `releasing` batch) so it never saw the batch turn
 * release_blocked. It said "All 3 orders were sent" when one was sent.
 *
 * It now reads the SERVER's per-order outcome (`outcome`, from
 * `getReleaseOutcome`) for the LIVE batch:
 *
 *   sent      -- the order's release item is `done` (`deliveredOrderIds`).
 *   held back -- the order has a stranded release item (refused, declined,
 *                still failing) or is a legacy charge-failed order; each gets
 *                its plain reason and whether any money was taken.
 *   not sent  -- everything else: no release item, so nothing was attempted.
 *
 * Until `outcome` arrives (batch still `releasing`) the screen shows progress
 * and NO verdict.
 *
 * ADR 0066 (4): an order that cannot be sent is held back and the batch still
 * goes. That is a normal outcome, so a partial release keeps the split frame
 * rather than becoming an error screen.
 *
 * WHERE HELD-BACK ORDERS ARE, as amended 2026-09-12 (ADR 0066 decision 4, and
 * `batch_release.py`): they STAY IN THIS BATCH. Release no longer moves them
 * anywhere, because the right destination is not knowable at release time --
 * it depends on when the shopper fixes their card, which may be days away.
 * Routing happens later, in `rebatch_order_after_payment_method_update`.
 *
 * This screen therefore names THIS batch. It previously said they "have moved
 * to the next open batch" and named a destination; with `destination_batch_id`
 * now always None on this path, `nextBatchId` arrives empty and that copy
 * rendered literally "moved into batch , which is open" -- wrong about the
 * behaviour AND broken in the render, pointing an operator at a batch that
 * does not exist while they are trying to recover money. The `nextBatchId`
 * prop and its two controls are kept for the case where a destination IS
 * named, and are guarded so they cannot render without one.
 */
import { money, orderTotal } from "@/services/batchManagementService";
import { chargeFacts, chargedSentence } from "./batchStatus";

const SENT_PREVIEW_LIMIT = 6;

export default {
  name: "ReleaseResult",
  props: {
    modelValue: { type: Boolean, default: false },
    // The LIVE batch (the panel passes it out of its list, so it changes as
    // the drain finishes).
    batch: { type: Object, required: true },
    // { items: [normalized stranded release items], deliveredOrderIds: [pk] }
    // or null while the outcome is not known.
    outcome: { type: Object, default: null },
    // The outcome read failed: say so instead of guessing.
    outcomeError: { type: Boolean, default: false },
    nextBatchId: { type: String, default: "" },
  },
  emits: ["update:modelValue", "open-next"],
  computed: {
    known() {
      return (
        !this.outcomeError &&
        this.batch.status !== "releasing" &&
        this.outcome != null
      );
    },
    released() {
      return this.batch.status === "released";
    },
    // An order the distributor canceled before release is neither sent nor
    // held back; it is not part of this release.
    orders() {
      return (this.batch.orders || []).filter(
        (o) => o.paymentState !== "canceled"
      );
    },
    deliveredIds() {
      return new Set(((this.outcome || {}).deliveredOrderIds || []).map(Number));
    },
    itemsByOrderId() {
      const m = new Map();
      for (const it of (this.outcome || {}).items || []) {
        m.set(Number(it.orderId), it);
      }
      return m;
    },
    sent() {
      return this.orders.filter((o) => this.deliveredIds.has(Number(o.rawId)));
    },
    heldBack() {
      const out = [];
      for (const o of this.orders) {
        if (this.deliveredIds.has(Number(o.rawId))) continue;
        const item = this.itemsByOrderId.get(Number(o.rawId));
        if (item) {
          out.push({ order: o, ...this.describeItem(item) });
        } else if (o.held) {
          // Legacy: a card decline recorded on the order with no item row.
          out.push({
            order: o,
            reason: o.reason || "Card declined",
            moneyLine: "No money was taken for this one.",
            cardDeclined: true,
          });
        }
      }
      return out;
    },
    notSent() {
      const heldIds = new Set(this.heldBack.map((h) => h.order.id));
      return this.orders.filter(
        (o) => !this.deliveredIds.has(Number(o.rawId)) && !heldIds.has(o.id)
      );
    },
    anyCardDeclined() {
      return this.heldBack.some((h) => h.cardDeclined);
    },
    sentPreview() {
      return this.sent.slice(0, SENT_PREVIEW_LIMIT);
    },
    sentTotal() {
      return this.sent.reduce((s, o) => s + orderTotal(o), 0);
    },
    heldBackTotal() {
      return this.heldBack.reduce((s, h) => s + orderTotal(h.order), 0);
    },
    // One shared definition with the batch row (batchStatus.chargeFacts).
    chargeFacts() {
      return chargeFacts(
        this.orders,
        [...this.deliveredIds],
        (this.outcome || {}).items || []
      );
    },
    allSent() {
      return this.heldBack.length === 0 && this.notSent.length === 0;
    },
    tone() {
      if (!this.known) return this.outcomeError ? "blocked" : "progress";
      if (this.released && this.allSent) return "success";
      return this.sent.length > 0 ? "split" : "blocked";
    },
    icon() {
      return { progress: "sync", success: "check", split: "call_split", blocked: "report_problem" }[this.tone];
    },
    headline() {
      const id = this.batch.id;
      if (this.outcomeError) return `Batch ${id}: we could not read the result`;
      if (!this.known) return `Releasing batch ${id}\u2026`;
      const total = this.orders.length;
      const unsent = this.heldBack.length + this.notSent.length;
      if (this.released && this.allSent) return `Batch ${id} released`;
      if (this.released) {
        return `Batch ${id} released \u2014 ${unsent} ${unsent === 1 ? "order" : "orders"} not sent`;
      }
      return `Batch ${id} is not released \u2014 ${this.sent.length} of ${total} orders sent`;
    },
    subline() {
      const t = this.batch.decoratorTarget;
      const total = this.orders.length;
      if (this.outcomeError) {
        return "The release was started, but we could not read what it did. Check the batch before you retry anything.";
      }
      if (!this.known) {
        return `Sending ${total} ${total === 1 ? "order" : "orders"} to ${t}. Nothing is final until this finishes.`;
      }
      if (this.released && this.allSent) {
        return `All ${total} orders were sent to ${t}.`;
      }
      const parts = [`${this.sent.length} of ${total} orders were sent to ${t}.`];
      if (this.heldBack.length) {
        parts.push(`${this.heldBack.length} held back and stays in this batch.`);
      }
      if (this.notSent.length) {
        parts.push(`${this.notSent.length} not sent because ${this.notSent.length === 1 ? "it was" : "they were"} not part of this release.`);
      }
      parts.push(chargedSentence(this.chargeFacts));
      return parts.join(" ");
    },
  },
  methods: {
    money,
    orderTotal,
    close() {
      this.$emit("update:modelValue", false);
    },
    describeItem(item) {
      const charged = item.chargedCents > 0;
      return {
        reason:
          item.lastErrorSummary ||
          item.lastError ||
          (charged
            ? "The decorator has not accepted this order yet."
            : "This order has not been sent."),
        moneyLine: charged
          ? `${money(item.chargedCents / 100)} charged, not sent.`
          : "No money was taken for this one.",
        cardDeclined: item.lastError === "card_declined",
      };
    },
  },
};
</script>

<style scoped>
.bm-overlay {
  background: var(--tf-surface);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.bm-result__head {
  padding: 26px 24px 20px;
  border-bottom: 1px solid var(--tf-border);
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.bm-result__badge {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
}

.bm-result__badge--split {
  background: var(--tf-chip-pending-bg);
  border-color: var(--tf-chip-pending-border);
  color: var(--tf-chip-pending-fg);
}

.bm-result__badge--progress {
  background: var(--tf-surface-light);
  border-color: var(--tf-border);
  color: var(--tf-fg-3);
}

.bm-result__counts {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 18px;
  font-size: 14px;
  color: var(--tf-fg-2);
}

.bm-result__count {
  font-size: 20px;
  font-weight: 500;
  color: var(--tf-fg-1);
  margin-right: 4px;
}

.bm-heldback__money {
  font-size: 12.5px;
  color: var(--tf-fg-3);
  margin-top: 2px;
}

.bm-result__badge--success {
  background: var(--tf-success-soft);
  border-color: var(--tf-chip-success-border);
  color: var(--tf-success-dark);
}

.bm-result__headings {
  min-width: 0;
}

.bm-result__title {
  font-size: 23px;
  font-weight: 500;
  color: var(--tf-secondary);
  line-height: 1.25;
  margin: 0;
}

.bm-result__sub {
  font-size: 13.5px;
  color: var(--tf-fg-3);
  margin: 6px 0 0;
  line-height: 1.55;
  max-width: 560px;
}

.bm-overlay__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 20px 24px;
}

.bm-heldback {
  border: 1px solid var(--tf-chip-error-border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 18px;
}

.bm-heldback__head {
  background: var(--tf-error-soft-alt);
  padding: 12px 16px;
}

.bm-heldback__title {
  font-size: 14.5px;
  font-weight: 500;
  color: var(--tf-chip-error-fg);
}

.bm-heldback__body {
  font-size: 12.5px;
  color: var(--tf-chip-error-fg);
  margin: 4px 0 0;
  line-height: 1.5;
}

.bm-heldback__body b {
  font-weight: 500;
}

.bm-heldback__row {
  padding: 13px 16px;
  border-top: 1px solid var(--tf-chip-error-border);
  display: flex;
  gap: 12px;
  align-items: center;
}

.bm-heldback__who {
  flex: 1;
  min-width: 0;
}

.bm-heldback__name {
  font-size: 14px;
  color: var(--tf-fg-1);
  font-weight: 500;
}

.bm-heldback__reason {
  font-size: 12.5px;
  color: var(--tf-error-dark);
  margin-top: 3px;
}

.bm-heldback__actions {
  display: flex;
  gap: 8px;
}

.bm-sentblock {
  border: 1px solid var(--tf-border);
  border-radius: 12px;
  overflow: hidden;
}

.bm-sentblock__head {
  background: var(--tf-surface-light);
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.bm-sentblock__title,
.bm-sentblock__amt {
  font-size: 14.5px;
  font-weight: 500;
  color: var(--tf-fg-1);
}

.bm-sentblock__row {
  display: flex;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid var(--tf-border);
  font-size: 13.5px;
}

.bm-sentblock__meta {
  color: var(--tf-fg-4);
  font-size: 12px;
}

.bm-sentblock__rowamt {
  font-weight: 500;
}

.bm-sentblock__more {
  padding: 10px 16px;
  border-top: 1px solid var(--tf-border);
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
}

.bm-result__lock {
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
  margin: 14px 0 0;
  line-height: 1.6;
  display: flex;
  gap: 8px;
}

.bm-result__lockicon {
  font-size: 16px;
  margin-top: 1px;
  color: var(--tf-fg-4);
  flex-shrink: 0;
}

.bm-overlay__foot {
  flex-shrink: 0;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-surface);
  padding: 14px 24px;
}

.bm-result__foot {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.bm-btn {
  border-radius: 9999px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 44px;
  padding: 9px 18px;
  font-size: 13px;
  border: 1px solid var(--tf-border-strong);
  background: var(--tf-surface);
  color: var(--tf-fg-2);
}

.bm-btn--primary {
  background: var(--tf-primary);
  border-color: var(--tf-primary);
  color: var(--tf-fg-on-primary);
}

.bm-btn__icon {
  font-size: 16px;
}

@media (max-width: 599px) {
  .bm-result__head,
  .bm-overlay__body,
  .bm-overlay__foot {
    padding-left: 16px;
    padding-right: 16px;
  }

  .bm-result__title {
    font-size: 20px;
  }

  .bm-heldback__row {
    flex-direction: column;
    align-items: flex-start;
  }

  .bm-result__foot .bm-btn {
    width: 100%;
  }
}
</style>
